import { useRef, useState } from 'react';

/**
 * Focal-point picker for a member photo.
 *
 * The user clicks/drags on the full photo to mark the focal point (e.g. a face);
 * the value is stored as percentages (0-100) and applied elsewhere as CSS
 * object-position. An optional "Auto-detect face" button uses the browser's
 * built-in FaceDetector when available and falls back gracefully otherwise.
 *
 * Props:
 *   src     - photo URL (required to show the picker)
 *   x, y    - current focal percentages (nullable; null = unset → treated as top)
 *   onChange(x, y) - called with rounded percentages when the point moves
 */
export default function PhotoFocusPicker({ src, x, y, onChange }) {
  const imgRef = useRef(null);
  const frameRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [autoState, setAutoState] = useState({ status: 'idle', message: '' });

  // Default the marker to the top-center crop we use when unset.
  const px = x == null ? 50 : x;
  const py = y == null ? 0 : y;

  if (!src) {
    return (
      <p className="text-sm text-gray-500">
        Upload a profile picture first to set its focal point.
      </p>
    );
  }

  const setFromEvent = (event) => {
    const frame = frameRef.current;
    if (!frame) return;
    const rect = frame.getBoundingClientRect();
    const clientX = event.touches?.[0]?.clientX ?? event.clientX;
    const clientY = event.touches?.[0]?.clientY ?? event.clientY;
    const nx = ((clientX - rect.left) / rect.width) * 100;
    const ny = ((clientY - rect.top) / rect.height) * 100;
    const clamp = (v) => Math.round(Math.min(100, Math.max(0, v)) * 2) / 2;
    onChange(clamp(nx), clamp(ny));
  };

  const autoDetect = async () => {
    if (!('FaceDetector' in window)) {
      setAutoState({
        status: 'unsupported',
        message: 'Auto-detect isn’t supported in this browser — drag the dot onto the face instead.',
      });
      return;
    }
    setAutoState({ status: 'working', message: 'Detecting face…' });
    try {
      // Load a fresh CORS-enabled copy so detection isn't blocked by tainting.
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = src;
      await img.decode();
      // eslint-disable-next-line no-undef
      const detector = new FaceDetector({ fastMode: true, maxDetectedFaces: 5 });
      const faces = await detector.detect(img);
      if (!faces?.length) {
        setAutoState({ status: 'none', message: 'No face found — set the focal point manually.' });
        return;
      }
      // Average the detected face-box centers, as % of the natural image size.
      const w = img.naturalWidth || 1;
      const h = img.naturalHeight || 1;
      let sx = 0;
      let sy = 0;
      for (const f of faces) {
        const b = f.boundingBox;
        sx += (b.x + b.width / 2) / w;
        sy += (b.y + b.height / 2) / h;
      }
      const clamp = (v) => Math.round(Math.min(100, Math.max(0, v)) * 2) / 2;
      onChange(clamp((sx / faces.length) * 100), clamp((sy / faces.length) * 100));
      setAutoState({ status: 'done', message: `Centered on ${faces.length > 1 ? `${faces.length} faces` : 'the face'}.` });
    } catch {
      setAutoState({
        status: 'error',
        message: 'Couldn’t auto-detect (image may block cross-origin reads) — set it manually.',
      });
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
        {/* Clickable full photo */}
        <div
          ref={frameRef}
          className="relative w-full max-w-xs cursor-crosshair select-none overflow-hidden rounded-lg border bg-gray-100"
          onPointerDown={(e) => { e.currentTarget.setPointerCapture?.(e.pointerId); setDragging(true); setFromEvent(e); }}
          onPointerMove={(e) => { if (dragging) setFromEvent(e); }}
          onPointerUp={() => setDragging(false)}
          onPointerCancel={() => setDragging(false)}
        >
          <img
            ref={imgRef}
            src={src}
            alt="Set focal point"
            draggable={false}
            className="block max-h-80 w-full object-contain"
          />
          {/* Focal marker */}
          <div
            className="pointer-events-none absolute h-6 w-6 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-[0_0_0_2px_rgba(0,0,0,0.4)] ring-2 ring-NBNA-primary"
            style={{ left: `${px}%`, top: `${py}%` }}
          >
            <div className="absolute left-1/2 top-1/2 h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-NBNA-primary" />
          </div>
        </div>

        {/* Live crop preview (matches the square directory card) */}
        <div className="shrink-0">
          <div className="text-xs font-medium text-gray-600 mb-1">Directory preview</div>
          <div className="h-32 w-32 overflow-hidden rounded-lg border bg-gray-100">
            <img
              src={src}
              alt="Crop preview"
              draggable={false}
              style={{ objectPosition: `${px}% ${py}%` }}
              className="h-full w-full object-cover filter grayscale"
            />
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          className="btn-secondary"
          onClick={autoDetect}
          disabled={autoState.status === 'working'}
        >
          {autoState.status === 'working' ? 'Detecting…' : 'Auto-detect face'}
        </button>
        <button
          type="button"
          className="text-sm text-NBNA-primary underline"
          onClick={() => onChange(50, 0)}
        >
          Reset to top
        </button>
        <span className="text-xs text-gray-500">Focal point: {px}% / {py}%</span>
      </div>
      {autoState.message ? <p className="text-xs text-gray-500">{autoState.message}</p> : null}
    </div>
  );
}
